import { useId, useRef, useState } from 'react';

import { ApiError } from '@shared/api/client';
import { artworkUrl } from '@shared/assets/artwork';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { Alert } from '@shared/ui/Alert';

import { peopleErrors } from '../_shared/errors';
import { useClearPortrait, useUploadPortrait } from '../_shared/mutations';
import { usePortraitPolicy } from '../_shared/queries';
import type { CreditProfileRecord } from '../_shared/types';

import { copy } from './copy';

/**
 * The credited person's portrait.
 *
 * **The column was always there and nothing could fill it.** `portrait_url` has
 * been on `content__credit_profiles` since the bootstrap and both operator write
 * routes accept it; the only writer that ever set one was the external-provider
 * fill, and this page said so in an info panel — «الصورة لا تُرفع في هذا
 * الإصدار». So a person created by hand, which is how most of Shahedni's cast
 * and crew exist, had no way to a face on their public page.
 *
 * **Every number this control states is read, not written.** The accepted
 * formats, the size cap and the minimum geometry come back from
 * `GET /uploadPolicy?subjectType=CREDIT_PROFILE` — all three are operator-owned
 * configuration rows, and a literal here would be silently wrong the day one of
 * them changes.
 *
 * **The file input is hidden behind a real button rather than styled.** A bare
 * `<input type="file">` cannot be given the console's own control shape without
 * vendor pseudo-elements that differ per engine; the button owns the label and
 * the focus ring, and the input keeps the file dialog. The input is
 * `aria-hidden` from the accessibility tree because the button already names the
 * action — exposing both announces the same control twice.
 *
 * **The preview is the stored URL, never a local object URL.** A blob preview
 * would show the operator their own file and hide the one failure this chain
 * actually has: the crop landing in the bucket while the write-back does not.
 * What renders here is what the public page will render.
 *
 * **And it is resolved through `artworkUrl`.** The service stores a ROOT-RELATIVE
 * path — `/assets-artwork/public/credit_profile/<ulid>/portrait-<ulid>` — the
 * same shape it stores a title's cover in. That path resolves on the storage
 * origin and nowhere else: on this console's own host it falls through to the
 * SPA's `index.html`, so an `<img>` pointed at it renders a broken image with a
 * `200 text/html` behind it. Measured on develop before this line existed.
 */
export function PersonDetailPortrait({ person }: { person: CreditProfileRecord }) {
  const inputId = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const policy = usePortraitPolicy();
  const upload = useUploadPortrait(person.slug);
  const clear = useClearPortrait(person.slug);

  const busy = upload.isPending || clear.isPending;
  const portraitUrl = artworkUrl(person.portrait_url);

  function report(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    setFailure(resolveErrorCopy(code, status, peopleErrors).message);
  }

  function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input straight away, so choosing the *same* file twice after a
    // failure still fires a change event.
    event.target.value = '';
    if (!file) return;

    setFailure(null);
    setSaved(false);

    upload.mutate(
      { profileId: person.id, file },
      {
        onSuccess: (url) => {
          if (url === null) {
            // The chain succeeded and the derivation had not landed inside the
            // window. Saying «رُفعت» here would be a claim the page cannot show.
            setFailure(copy.fields.portrait.failed);
            return;
          }
          setSaved(true);
        },
        onError: report,
      },
    );
  }

  function onClear() {
    setFailure(null);
    setSaved(false);
    clear.mutate({ profileId: person.id }, { onError: report });
  }

  return (
    <div className="grid gap-3">
      <p className="mb-0 text-sm font-semibold text-ink">{copy.fields.portrait.title}</p>
      <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.fields.portrait.body}</p>

      <div className="flex flex-wrap items-center gap-4">
        {/*
          `size-[5rem]`, NOT `size-20`. This repo's `tailwind.config.ts` sets
          `theme.spacing` OUTSIDE `extend`, so the scale stops at step 10 and any
          class above it emits NO CSS at all. `size-20` therefore sized nothing:
          the circle collapsed onto its content and `size-full` on the `<img>`
          resolved against an auto-sized box, so a 996×996 portrait rendered at
          996×996 — a face the width of the console. Measured on develop.
        */}
        <span className="grid size-[5rem] shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-surface-raised">
          {portraitUrl ? (
            <img
              src={portraitUrl}
              alt={copy.fields.portrait.alt(person.display_name)}
              className="size-full object-cover"
            />
          ) : (
            <span className="px-2 text-center text-[0.625rem] leading-tight text-ink-muted">
              {copy.fields.portrait.none}
            </span>
          )}
        </span>

        <div className="grid gap-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="hit-target rounded-sm border border-border bg-surface px-4 text-sm font-semibold text-ink hover:bg-hover-bg disabled:cursor-not-allowed disabled:opacity-45"
            >
              {portraitUrl ? copy.fields.portrait.replace : copy.fields.portrait.choose}
            </button>

            {portraitUrl ? (
              <button
                type="button"
                disabled={busy}
                onClick={onClear}
                className="hit-target rounded-sm border border-border bg-surface px-4 text-sm text-ink-muted hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-45"
              >
                {copy.fields.portrait.remove}
              </button>
            ) : null}
          </div>

          <p id={hintId} className="mb-0 max-w-measure text-xs text-ink-muted">
            {copy.fields.portrait.hint(
              (policy.data?.accepted_formats ?? []).join(' · '),
              formatNumber(bytesToMegabytes(policy.data?.max_size_bytes)),
              formatNumber(policy.data?.min_width ?? 0),
              formatNumber(policy.data?.min_height ?? 0),
            )}
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={(policy.data?.accepted_formats ?? []).join(',')}
        aria-hidden="true"
        tabIndex={-1}
        onChange={onPick}
        className="sr-only"
      />

      {/*
        Both regions live in the markup from load and are revealed by the
        handler, for the reason the save confirmation beside them records: a live
        region revealed and populated in one frame is announced unreliably.
      */}
      <div aria-live="polite">
        {upload.isPending ? (
          <p className="mb-0 text-xs text-ink-muted">{copy.fields.portrait.uploading}</p>
        ) : null}
        {saved ? <p className="text-success-text mb-0 text-xs">{copy.fields.portrait.uploaded}</p> : null}
      </div>

      {failure ? <Alert kind="danger">{failure}</Alert> : null}
    </div>
  );
}

/** The cap is published in megabytes and held in bytes; the label wants it back. */
function bytesToMegabytes(bytes: number | null | undefined): number {
  return typeof bytes === 'number' ? Math.round(bytes / 1_048_576) : 0;
}
